import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../config/api';
import '../styles/NewTradeDialog.css';

const NewTradeDialog = ({ isOpen, onClose, onTradeCreated, prefillData }) => {
  const [formData, setFormData] = useState({
    name: '',
    symbolId: '',
    exchange1Id: '',
    exchange1Contract: 'SPOT',
    exchange2Id: '',
    exchange2Contract: 'SPOT',
    notes: ''
  });

  const [symbols, setSymbols] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isPrefilled, setIsPrefilled] = useState(false);

  // Drag state
  const [position, setPosition] = useState({ x: window.innerWidth / 2 - 250, y: window.innerHeight / 2 - 300 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [, setHasDragged] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchSymbols();
      fetchExchanges();
      setIsPrefilled(false);
      // Reset position to center when dialog opens
      setPosition({
        x: window.innerWidth / 2 - 250,
        y: window.innerHeight / 2 - 300
      });
    }
  }, [isOpen]);

  // Prefill form when symbols and exchanges are loaded and prefillData is available
  useEffect(() => {
    if (prefillData && symbols.length > 0 && exchanges.length > 0 && !isPrefilled) {
      // Find matching symbol by name
      const matchingSymbol = symbols.find(s =>
        s.symbol && s.symbol.toLowerCase() === prefillData.symbol.toLowerCase()
      );

      // Find matching exchanges by name
      const matchingEx1 = exchanges.find(e =>
        e.exchange_name && e.exchange_name.toLowerCase() === prefillData.ex1.toLowerCase()
      );
      const matchingEx2 = exchanges.find(e =>
        e.exchange_name && e.exchange_name.toLowerCase() === prefillData.ex2.toLowerCase()
      );

      // Update form data if matches found
      if (matchingSymbol || matchingEx1 || matchingEx2) {
        setFormData(prev => ({
          ...prev,
          symbolId: matchingSymbol ? matchingSymbol.id.toString() : prev.symbolId,
          exchange1Id: matchingEx1 ? matchingEx1.id.toString() : prev.exchange1Id,
          exchange1Contract: prefillData.ex1Contract || prev.exchange1Contract,
          exchange2Id: matchingEx2 ? matchingEx2.id.toString() : prev.exchange2Id,
          exchange2Contract: prefillData.ex2Contract || prev.exchange2Contract
        }));
        setIsPrefilled(true);
      }
    }
  }, [prefillData, symbols, exchanges, isPrefilled]);

  const fetchSymbols = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/symbols/active`);
      const data = await response.json();
      if (data.success) {
        setSymbols(data.data);
      }
    } catch (error) {
      console.error('Error fetching symbols:', error);
    }
  };

  const fetchExchanges = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/exchanges/active`);
      const data = await response.json();
      if (data.success) {
        setExchanges(data.data);
      }
    } catch (error) {
      console.error('Error fetching exchanges:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/trades`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        onTradeCreated(data.data);
        onClose();
        setFormData({
          name: '',
          symbolId: '',
          exchange1Id: '',
          exchange1Contract: 'SPOT',
          exchange2Id: '',
          exchange2Contract: 'SPOT',
          notes: ''
        });
      } else {
        setError(data.error || 'Failed to create trade');
      }
    } catch (error) {
      console.error('Error creating trade:', error);
      setError('Failed to create trade');
    } finally {
      setLoading(false);
    }
  };

  // Drag handlers
  const handleMouseDown = (e) => {
    if (e.target.closest('.dialog-header')) {
      setIsDragging(true);
      setHasDragged(false);
      setDragOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setHasDragged(true);
      setPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add mouse event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, dragOffset]);

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('dialog-overlay')) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="dialog-overlay" onClick={handleOverlayClick}>
      <div
        className="dialog-content"
        onMouseDown={handleMouseDown}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          cursor: isDragging ? 'grabbing' : 'default'
        }}
      >
        <div className="dialog-header" style={{ cursor: isDragging ? 'grabbing' : 'grab' }}>
          <h2>Create New Trade</h2>
          <button className="dialog-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="dialog-form">
          {error && <div className="dialog-error">{error}</div>}

          <div className="form-group">
            <label htmlFor="name">Name:</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter trade name (optional)"
            />
          </div>

          <div className="form-group">
            <label htmlFor="symbolId">Symbol:</label>
            <select
              id="symbolId"
              name="symbolId"
              value={formData.symbolId}
              onChange={handleChange}
              required
            >
              <option value="">Select symbol...</option>
              {symbols.map(symbol => (
                <option key={symbol.id} value={symbol.id}>
                  {symbol.symbol} - {symbol.symbol_name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-section">
            <h3>Exchange 1</h3>
            <div className="form-row">
              <div className="form-group exchange-group">
                <label htmlFor="exchange1Id">Exchange:</label>
                <select
                  id="exchange1Id"
                  name="exchange1Id"
                  value={formData.exchange1Id}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select exchange...</option>
                  {exchanges.map(exchange => (
                    <option key={exchange.id} value={exchange.id}>
                      {exchange.exchange_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group contract-group">
                <label htmlFor="exchange1Contract">Contract Type:</label>
                <select
                  id="exchange1Contract"
                  name="exchange1Contract"
                  value={formData.exchange1Contract}
                  onChange={handleChange}
                  required
                >
                  <option value="SPOT">SPOT</option>
                  <option value="LONG">LONG</option>
                  <option value="SHORT">SHORT</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Exchange 2</h3>
            <div className="form-row">
              <div className="form-group exchange-group">
                <label htmlFor="exchange2Id">Exchange:</label>
                <select
                  id="exchange2Id"
                  name="exchange2Id"
                  value={formData.exchange2Id}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select exchange...</option>
                  {exchanges.map(exchange => (
                    <option key={exchange.id} value={exchange.id}>
                      {exchange.exchange_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group contract-group">
                <label htmlFor="exchange2Contract">Contract Type:</label>
                <select
                  id="exchange2Contract"
                  name="exchange2Contract"
                  value={formData.exchange2Contract}
                  onChange={handleChange}
                  required
                >
                  <option value="SPOT">SPOT</option>
                  <option value="LONG">LONG</option>
                  <option value="SHORT">SHORT</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes:</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Enter notes (optional)"
              rows="4"
            />
          </div>

          <div className="dialog-actions">
            <button type="button" onClick={onClose} className="btn-cancel">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn-submit">
              {loading ? 'Creating...' : 'Create Trade'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewTradeDialog;
